<template>
  <UCard
    as="a"
    :href="cottageDetailUrl"
    :ui="mapCard"
    :target="isMobile ? '_self' : '_blank'"
  >
    <div class="relative">
      <CottageListFavorite
        :item="{
          id: cottage.id,
          name: cottage.name,
          province: {
            slug: cottage.province.slug,
            name: cottage.province.name || cottage.province.slug,
            url: '',
          },
          village: {
            slug: cottage.village.slug,
            name: cottage.village.name,
          },
          media: photoItems,
        }"
        :top="2"
        :right="10"
        button-size="xs"
      />
      <UButton
        v-if="showClose"
        class="absolute top-2 right-2 z-10"
        :aria-label="t('a11y.close-button')"
        variant="subtle"
        color="neutral"
        icon="i-heroicons-x-mark"
        size="xs"
        @click.prevent="$emit('close')"
      />
      <div v-if="numberOfPhotoItems > 1" class="absolute bottom-4 right-4 z-10">
        <span
          class="inline-flex items-center text-sm px-2.5 py-1.5 bg-black/60 text-white font-bold rounded-full"
        >
          {{
            t('views.cottage.gallery.legend', {
              current: currentCarouselPage,
              total: numberOfPhotoItems,
            })
          }}
        </span>
      </div>

      <UCarousel
        :key="carouselKey"
        :items="photoItems"
        :ui="fullCarouselWithAlwaysArrows"
        :arrows="true"
        @select="updateCurrentCarouselPage"
        @click.stop.prevent="() => {}"
      >
        <template #default="media">
          <div class="w-full h-full aspect-w-8 aspect-h-4 md:aspect-h-5 m-auto">
            <TLazyimg
              :src="media.item.url"
              :alt="media.item.alt"
              @click="handleImageClick"
            />
          </div>
        </template>
      </UCarousel>
    </div>

    <template #footer>
      <div class="px-3 pb-3">
        <TText
          tag="h2"
          :size="isMobile ? 'xs' : 'sm'"
          variant="semibold"
          classes="mb-0 truncate font-sans"
        >
          {{ cottage.name }}
        </TText>

        <TText
          color="text-gray-600"
          :size="isMobile ? 'xs' : 'sm'"
          classes="mb-0 font-sans"
        >
          {{ cottage.village.name }}
        </TText>
        <div class="flex items-center">
          <TStars :stars="cottage.review.score" />
          <TText
            :size="isMobile ? 'xs' : 'sm'"
            class="ml-1"
            classes="mb-0 font-sans"
            color="text-gray-600"
            >({{ cottage.review.numReviews }})</TText
          >
        </div>

        <div class="inline-flex items-center gap-x-1 flex-wrap">
          <UBadge
            v-if="cottage.hasFreeCancellation"
            color="success"
            variant="subtle"
            :size="isMobile ? 'xs' : 'sm'"
            >{{
              t('views.cottage-list.cottageCard.tags.freeCancellation')
            }}</UBadge
          >
          <div v-if="cottage.hasDeals" class="flex gap-x-1 text-center">
            <UBadge
              color="error"
              variant="subtle"
              :size="isMobile ? 'xs' : 'sm'"
              icon="i-lucide-tag"
            >
              <span v-if="cottage.currentDeal !== null">{{
                t('views.cottage-list.cottageCard.deal')
              }}</span>
              <span v-else>{{
                t('views.cottage-list.cottageCard.dealAvailable')
              }}</span>
            </UBadge>
            <TText
              v-if="cottage.currentDeal !== null"
              :size="isMobile ? 'xs' : 'sm'"
              color="text-gray-900"
              classes="mb-0 line-through font-sans"
            >
              {{ toEuro(cottage.currentDeal.oldPrice.amount) }}
            </TText>
          </div>
          <TText
            :size="isMobile ? 'xs' : 'sm'"
            variant="semibold"
            color="text-gray-900"
            classes="mb-0 inline font-sans"
          >
            {{ displayPrice }}
          </TText>
        </div>
      </div>
    </template>
  </UCard>
</template>

<script setup lang="ts">
import type { MapCottageType } from '~~/shared/api/rurality/v3/search/cottages/map/schema'
import { browserFetch as fetchCottageGallery } from '~~/modules/api/rurality/v3/search/cottage-gallery'
import type { MediaType } from '~~/shared/api/rurality/v3/search/cottage-gallery/schema'
import { fullCarouselWithAlwaysArrows } from '~~/modules/design-system/utils/nuxt-ui-config/components/carousel'
import { mapCard } from '~~/modules/design-system/utils/nuxt-ui-config/components/card'

const { isMobile } = useDevice()

const props = defineProps<{
  cottage: MapCottageType
  showClose?: boolean
}>()

defineEmits<{
  close: []
}>()

const route = useRoute()

const cottageDetailUrl = computed(() =>
  routes().cottageDetailFromCottageList({
    slug: props.cottage.slug,
    province: props.cottage.province.slug,
    d: route.query.d as string | undefined,
    c: route.query.c as string | undefined,
  }),
)

const currentCarouselPage = ref(1)
const carouselKey = ref(0)

const photoItems = ref<
  Array<{
    type: 'PHOTO'
    url: string
    alt: string
    width: number
    height: number
  }>
>([])

const getPhotoItems = (items: MediaType[]) => {
  return items
    .filter(item => item.type === 'PHOTO')
    .map(item => ({
      type: 'PHOTO' as const,
      url: item.url,
      alt: item.alt || props.cottage.name,
      width: item.width!,
      height: item.height!,
    }))
}

const loadCottageGallery = async () => {
  currentCarouselPage.value = 1
  carouselKey.value++

  const initialPhotoItem = {
    type: 'PHOTO' as const,
    url: props.cottage.media.thumb,
    alt: props.cottage.name,
    width: 1600,
    height: 800,
  }

  photoItems.value = [initialPhotoItem]

  try {
    const response = await fetchCottageGallery({ id: props.cottage.id })

    if (response.success && response.success.data) {
      const galleryPhotos = getPhotoItems(response.success.data.medias)

      if (galleryPhotos.length > 0) {
        photoItems.value = galleryPhotos
      }
    }
  } catch (error) {
    console.error('Error fetching cottage gallery:', error)
  }
}

watch(
  () => props.cottage.id,
  () => {
    loadCottageGallery()
  },
  { immediate: true },
)

const updateCurrentCarouselPage = (index: number) => {
  currentCarouselPage.value = index + 1
}

const numberOfPhotoItems = computed(() => {
  return photoItems.value.length
})

const displayPrice = computed(() => {
  if (props.cottage.hasDeals && props.cottage.currentDeal) {
    return toEuro(props.cottage.currentDeal.newPrice.amount)
  } else if (props.cottage.price) {
    if (props.cottage.priceType === 'EXACT') {
      return toEuro(props.cottage.price.amount)
    }
    return t('views.cottage-list.cottageCard.priceTotal', {
      n: toEuro(props.cottage.price.amount),
    })
  } else {
    return toEuro(props.cottage.averagePrice?.amount)
  }
})

const handleImageClick = () => {
  window.open(cottageDetailUrl.value, isMobile ? '_self' : '_blank')
}
</script>
